import { apisStore } from "context/GlobalStates";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import {
  DataTable,
  DataTableFilterMeta,
  DataTableFilterMetaData,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { Messages } from "primereact/messages";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataApi, DataPoint } from "serviceapi/dataPusherApi";
import InputsOutputsHistory from "./InputsOutputsHistory";
import { InputNumber } from "primereact/inputnumber";
import NotificationPopUp from "util/NotificationPopUp";
import { InputText } from "primereact/inputtext";

interface LatestInputsOutputsProps {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  selectedPipelineId: string;
}

export enum DataType {
  INPUT = "input",
  OUTPUT = "output",
}

const LatestInputsOutputs = ({
  isVisible,
  setIsVisible,
  selectedPipelineId,
}: LatestInputsOutputsProps) => {
  const [data, setData] = useState<{ [key: string]: DataPoint } | null>(null);
  const [dataHistory, setDataHistory] = useState<DataPoint[] | null>(null);
  const [selectedDataName, setSelectedDataName] = useState<string>("");
  const [isDataHistoryDialogVisible, setIsDataHistoryDialogVisible] =
    useState<boolean>(false);
  const [selectedDataType, setSelectedDataType] = useState<DataType>(
    DataType.INPUT
  );
  const [updateFrequency, setUpdateFrequency] = useState<number>(5);
  const [lastRefreshTime, setLastRefreshTime] = useState<number | null>(null);
  const [tooltipText, setTooltipText] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
  });

  const msgs = useRef<Messages>(null);

  const apiInputs = useMemo(
    () => apisStore.getState()?.dataPusher?.dataApi as DataApi,
    []
  );

  const fetchDataHistory = useCallback(
    async (inputName: string) => {
      try {
        const response =
          selectedDataType === DataType.OUTPUT
            ? await apiInputs.getOutputHistory(inputName, selectedPipelineId)
            : await apiInputs.getInputHistory(inputName, selectedPipelineId);
        setDataHistory(response.data);
        setIsDataHistoryDialogVisible(true);

        msgs.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Successfully fetched history.",
        });
      } catch (error) {
        console.error("Error fetching data history:", error);
        msgs.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch data history. Please try again later.",
        });
      }
    },
    [apiInputs, selectedPipelineId, selectedDataType]
  );

  const fetchData = useCallback(async () => {
    try {
      const response =
        selectedDataType === DataType.OUTPUT
          ? await apiInputs.getLatestOutputs(selectedPipelineId)
          : await apiInputs.getLatestInputs(selectedPipelineId);
      setData(response.data);

      const currentTime = Date.now();
      setLastRefreshTime(currentTime);
    } catch (error) {
      console.error("Error fetching data:", error);
      msgs.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch data. Please try again later.",
      });
    }
  }, [apiInputs, selectedPipelineId, selectedDataType]);

  useEffect(() => {
    if (
      !isVisible ||
      isDataHistoryDialogVisible ||
      !updateFrequency ||
      updateFrequency <= 0
    )
      return;

    fetchData();
    const intervalId = setInterval(fetchData, updateFrequency * 1000);

    return () => clearInterval(intervalId);
  }, [isVisible, fetchData, isDataHistoryDialogVisible, updateFrequency]);

  const tableData = useMemo(
    () =>
      data
        ? Object.keys(data).map((key) => ({
            name: key,
            value: data[key].v,
          }))
        : [],
    [data]
  );

  const renderSelectInputButton = useCallback(
    (rowData: { name: string }) => (
      <Button
        icon="pi pi-history"
        className="p-button-info p-button-text custom-button"
        onClick={() => {
          fetchDataHistory(rowData.name);
          setSelectedDataName(rowData.name);
        }}
      />
    ),
    [fetchDataHistory]
  );

  const handleTabChange = useCallback(
    (e: { index: number }) =>
      setSelectedDataType(e.index === 0 ? DataType.INPUT : DataType.OUTPUT),
    []
  );

  const copyToClipboard = useCallback(() => {
    navigator.clipboard
      .writeText(JSON.stringify(dataHistory))
      .then(() => {
        NotificationPopUp.show("Copied to clipboard");
      })
      .catch(() => {
        NotificationPopUp.show("Failed to copy");
      });
  }, [dataHistory]);

  const onGlobalFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters((prevFilters) => ({
      ...prevFilters,
      global: { ...prevFilters.global, value },
    }));
  };

  const updateTooltipText = () => {
    if (!lastRefreshTime) {
      setTooltipText("Refresh manually\nLast refresh: Never");
    } else {
      const elapsedSeconds = Math.floor((Date.now() - lastRefreshTime) / 1000);
      setTooltipText(
        `Refresh manually\nLast refresh: ${elapsedSeconds} seconds ago`
      );
    }
  };

  return (
    <div>
      <Messages ref={msgs} style={{ position: "absolute", top: 0, right: 0 }} />
      <Dialog
        header={
          <div className="flex justify-content-between align-items-center">
            <div className="flex flex-row gap-2">
              <TabView
                activeIndex={selectedDataType === DataType.INPUT ? 0 : 1}
                onTabChange={handleTabChange}
              >
                <TabPanel header="Latest inputs" />
                <TabPanel header="Latest outputs" />
              </TabView>
              <InputText
                id="searchByName"
                type="search"
                placeholder="Search by name"
                className="w-10rem"
                value={
                  (filters["global"] as DataTableFilterMetaData)?.value || ""
                }
                onChange={(e) => onGlobalFilterChange(e)}
              />
            </div>
            <div className="flex align-items-center gap-2 mr-2">
              <Button
                icon="pi pi-refresh"
                className="p-button-text custom-button"
                tooltip={tooltipText}
                tooltipOptions={{
                  position: "bottom",
                }}
                onMouseEnter={updateTooltipText}
                onClick={fetchData}
              />

              <InputNumber
                value={updateFrequency}
                onChange={(e) => setUpdateFrequency(e.value as number)}
                tooltip="Refresh frequency in seconds"
                tooltipOptions={{ position: "bottom" }}
                suffix=" s"
                placeholder="s"
              />
            </div>
          </div>
        }
        visible={isVisible}
        style={{
          minWidth: "40rem",
          width: "30%",
          height: "100%",
          position: "absolute",
          bottom: 0,
          right: 0,
        }}
        modal={false}
        resizable
        onHide={() => setIsVisible(false)}
      >
        <DataTable
          value={tableData}
          size="small"
          dataKey="name"
          filterDisplay="menu"
          filters={filters}
          onFilter={(e) => setFilters(e.filters)}
        >
          <Column field="name" header="Name" style={{ width: "75%" }} />
          <Column field="value" header="Value" style={{ width: "15%" }} />
          <Column
            body={renderSelectInputButton}
            style={{ width: "10%", textAlign: "center" }}
          />
        </DataTable>
        {dataHistory && isDataHistoryDialogVisible && (
          <InputsOutputsHistory
            dataType={selectedDataType}
            dataHistory={dataHistory}
            isDataHistoryDialogVisible={isDataHistoryDialogVisible}
            setIsDataHistoryDialogVisible={setIsDataHistoryDialogVisible}
            refetchHistory={() => fetchDataHistory(selectedDataName)}
            copyToClipboard={() => copyToClipboard()}
          />
        )}
      </Dialog>
    </div>
  );
};

export default LatestInputsOutputs;
